<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentField from './AgentField.vue';
import AgentNotice from './AgentNotice.vue';
import AgentPublicationFeedback from './AgentPublicationFeedback.vue';
import AgentReadinessChecklist from './AgentReadinessChecklist.vue';
import AgentSection from './AgentSection.vue';
import { createSectionFormProxy } from './createSectionFormProxy';
import { apiDateTimeValue, formatApiDateTime } from '../datePresentation';

const props = defineProps({
  form: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  teams: { type: Array, default: () => [] },
  canManage: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
  readinessChecklist: { type: Array, default: () => [] },
  lifecycleStatus: { type: String, default: 'draft' },
  hasUnpublishedChanges: { type: Boolean, default: false },
  publishedVersionId: { type: [Number, String], default: null },
  publishedAt: { type: [Number, String], default: null },
  versions: { type: Array, default: () => [] },
  isLoadingVersions: { type: Boolean, default: false },
  versionsError: { type: String, default: '' },
  preflightBlockers: { type: Array, default: () => [] },
  preflightWarnings: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'updateField',
  'save',
  'openRoute',
  'retryVersions',
  'reviewPreflightIssue',
]);
const { t: ce } = useI18n();
const formModel = createSectionFormProxy(
  () => props.form,
  payload => emit('updateField', payload)
);

const qualificationModeOptions = [
  {
    value: 'pre_attendance',
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.QUALIFICATION_PRE_ATTENDANCE_LABEL'),
    helper: ce('CAPTAIN_EXPERIENCE.SCRIPT.QUALIFICATION_PRE_ATTENDANCE_HELPER'),
  },
  {
    value: 'auto_scheduling',
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.QUALIFICATION_AUTO_SCHEDULING_LABEL'),
    helper: ce(
      'CAPTAIN_EXPERIENCE.SCRIPT.QUALIFICATION_AUTO_SCHEDULING_HELPER'
    ),
  },
  {
    value: 'pre_scheduling_link',
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.QUALIFICATION_LINK_LABEL'),
    helper: ce('CAPTAIN_EXPERIENCE.SCRIPT.QUALIFICATION_LINK_HELPER'),
  },
];

const operationScopeOptions = [
  { value: 'both', label: ce('CAPTAIN_EXPERIENCE.SCRIPT.OPERATION_BOTH') },
  { value: 'rent', label: ce('CAPTAIN_EXPERIENCE.SCRIPT.OPERATION_RENT') },
  { value: 'sale', label: ce('CAPTAIN_EXPERIENCE.SCRIPT.OPERATION_SALE') },
];

const allowedChannelOptions = [
  {
    value: 'whatsapp',
    label: ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNEL_WHATSAPP'),
  },
  {
    value: 'website',
    label: ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNEL_WEBSITE'),
  },
  {
    value: 'instagram',
    label: ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNEL_INSTAGRAM'),
  },
  {
    value: 'facebook',
    label: ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNEL_FACEBOOK'),
  },
  {
    value: 'email',
    label: ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNEL_EMAIL'),
  },
  {
    value: 'api',
    label: ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNEL_API'),
  },
];

const normalizedLifecycleStatus = computed(() =>
  String(props.lifecycleStatus || 'draft').toLowerCase()
);
const isCurrentlyPublished = computed(() =>
  ['active', 'silent'].includes(normalizedLifecycleStatus.value)
);
const lifecycleLabel = computed(() => {
  const labels = {
    active: 'CAPTAIN_EXPERIENCE.WORKSPACE.LIFECYCLE_ACTIVE',
    silent: 'CAPTAIN_EXPERIENCE.WORKSPACE.LIFECYCLE_SILENT',
    paused: 'CAPTAIN_EXPERIENCE.WORKSPACE.LIFECYCLE_PAUSED',
    draft: 'CAPTAIN_EXPERIENCE.WORKSPACE.LIFECYCLE_DRAFT',
  };
  return ce(
    labels[normalizedLifecycleStatus.value] ||
      'CAPTAIN_EXPERIENCE.WORKSPACE.LIFECYCLE_DRAFT'
  );
});
const lifecycleClasses = computed(() => {
  const classes = {
    active: 'bg-n-teal-9/10 text-n-teal-11',
    silent: 'bg-n-brand/10 text-n-blue-11',
    paused: 'bg-n-amber-9/10 text-n-amber-11',
    draft: 'bg-n-alpha-2 text-n-slate-11',
  };
  return classes[normalizedLifecycleStatus.value] || classes.draft;
});
const saveLabel = computed(() => {
  if (formModel.publicationMode === 'draft') {
    return ce(
      isCurrentlyPublished.value
        ? 'CAPTAIN_EXPERIENCE.WORKSPACE.UNPUBLISH_AGENT'
        : 'CAPTAIN_EXPERIENCE.WORKSPACE.SAVE_DRAFT'
    );
  }
  return ce(
    isCurrentlyPublished.value
      ? 'CAPTAIN_EXPERIENCE.WORKSPACE.PUBLISH_CHANGES'
      : 'CAPTAIN_EXPERIENCE.WORKSPACE.PUBLISH_AGENT'
  );
});
</script>

<template>
  <section id="agent-publication" class="flex flex-col gap-8" tabindex="-1">
    <AgentPublicationFeedback
      :blockers="preflightBlockers"
      :warnings="preflightWarnings"
      @review="emit('reviewPreflightIssue', $event)"
    />
    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.PUBLICATION_STATUS')"
    >
      <div
        class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
              :class="lifecycleClasses"
            >
              {{ lifecycleLabel }}
            </span>
            <span
              v-if="publishedVersionId"
              class="text-xs font-medium text-n-slate-10"
            >
              {{
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.PUBLISHED_VERSION', {
                  id: publishedVersionId,
                })
              }}
            </span>
          </div>
          <p class="mt-2 text-sm leading-relaxed text-n-slate-11">
            {{
              ce(
                isCurrentlyPublished
                  ? 'CAPTAIN_EXPERIENCE.WORKSPACE.PUBLICATION_LIVE_HELPER'
                  : 'CAPTAIN_EXPERIENCE.WORKSPACE.PUBLICATION_DRAFT_HELPER'
              )
            }}
          </p>
          <time
            v-if="publishedAt && formatApiDateTime(publishedAt)"
            :datetime="apiDateTimeValue(publishedAt)"
            class="mt-1 block text-xs text-n-slate-10"
          >
            {{
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.PUBLISHED_AT', {
                date: formatApiDateTime(publishedAt),
              })
            }}
          </time>
        </div>
        <Button
          color="slate"
          variant="faded"
          icon="i-lucide-play"
          :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.TEST_CURRENT_DRAFT')"
          @click="emit('openRoute', 'captain_assistants_playground_index')"
        />
      </div>

      <AgentNotice
        v-if="hasUnpublishedChanges"
        class="mt-4"
        tone="warning"
        :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNPUBLISHED_CHANGES_TITLE')"
        :description="
          ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNPUBLISHED_CHANGES_DESCRIPTION')
        "
      />
    </AgentSection>

    <AgentSection :title="ce('CAPTAIN_EXPERIENCE.AUTO.PUBLICACAO')">
      <form class="grid gap-4" @submit.prevent="emit('save')">
        <fieldset
          :disabled="!canManage"
          class="contents disabled:pointer-events-none disabled:opacity-70"
        >
          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.MODO_DE_QUALIFICACAO')"
            :helper="
              ce(
                'CAPTAIN_EXPERIENCE.AUTO.DEFINE_ATE_ONDE_A_IA_PODE_AVANCAR_ANTES_DA_EQUIPE'
              )
            "
            grouped
          >
            <div class="grid gap-2 md:grid-cols-3">
              <label
                v-for="option in qualificationModeOptions"
                :key="option.value"
                class="agent-mode-option"
                :class="
                  formModel.qualificationMode === option.value
                    ? 'border-n-brand bg-n-brand/10 text-n-blue-11'
                    : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand'
                "
              >
                <input
                  v-model="formModel.qualificationMode"
                  type="radio"
                  name="agent-qualification-mode"
                  :value="option.value"
                  class="sr-only"
                />
                <span class="text-sm font-bold">{{ option.label }}</span>
                <span
                  class="mt-1 block text-xs leading-relaxed text-n-slate-11"
                >
                  {{ option.helper }}
                </span>
              </label>
            </div>
          </AgentField>
          <AgentField
            v-if="formModel.qualificationMode === 'pre_scheduling_link'"
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.LINK_DE_PRE_AGENDAMENTO')"
            control-id="agent-pre-scheduling-link"
            :error="errors.preSchedulingLinkUrl"
            required
          >
            <input
              id="agent-pre-scheduling-link"
              v-model.trim="formModel.preSchedulingLinkUrl"
              class="agent-input"
              :placeholder="
                ce('CAPTAIN_EXPERIENCE.AUTO.HTTPS_AGENDA_SUAIMOBILIARIA_COM_BR')
              "
              type="url"
              required
              :aria-invalid="Boolean(errors.preSchedulingLinkUrl)"
              :aria-describedby="
                errors.preSchedulingLinkUrl
                  ? 'agent-pre-scheduling-link-description'
                  : undefined
              "
            />
          </AgentField>
          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.OPERACAO_ATENDIDA')"
            grouped
          >
            <div class="grid gap-2 md:grid-cols-3">
              <label
                v-for="option in operationScopeOptions"
                :key="option.value"
                class="agent-segment-option"
                :class="
                  formModel.operationScope === option.value
                    ? 'border-n-brand bg-n-brand/10 text-n-blue-11'
                    : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand'
                "
              >
                <input
                  v-model="formModel.operationScope"
                  type="radio"
                  name="agent-operation-scope"
                  :value="option.value"
                  class="sr-only"
                />
                {{ option.label }}
              </label>
            </div>
          </AgentField>
          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.MODO_DE_ATIVACAO')"
            control-id="agent-publication-mode"
          >
            <select
              id="agent-publication-mode"
              v-model="formModel.publicationMode"
              class="agent-input"
            >
              <option value="draft">
                {{ ce('CAPTAIN_EXPERIENCE.AUTO.RASCUNHO') }}
              </option>
              <option value="silent">
                {{ ce('CAPTAIN_EXPERIENCE.AUTO.MODO_SILENCIOSO') }}
              </option>
              <option value="active">
                {{ ce('CAPTAIN_EXPERIENCE.AUTO.RESPONDER_CLIENTES') }}
              </option>
            </select>
          </AgentField>
          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.PERCENTUAL_DE_CONVERSAS')"
            :helper="
              ce(
                'CAPTAIN_EXPERIENCE.AUTO.USE_0_PARA_MANTER_DESLIGADO_E_100_PARA_TODAS_AS_CONVERSA'
              )
            "
            control-id="agent-rollout-percent"
            :error="errors.rolloutPercent"
            required
          >
            <input
              id="agent-rollout-percent"
              v-model.number="formModel.rolloutPercent"
              class="agent-input"
              min="0"
              max="100"
              step="1"
              type="number"
              required
              inputmode="numeric"
              :disabled="formModel.publicationMode === 'draft'"
              :aria-invalid="Boolean(errors.rolloutPercent)"
              aria-describedby="agent-rollout-percent-description"
            />
            <p class="mt-2 text-xs leading-relaxed text-n-slate-11">
              {{
                ce(
                  'CAPTAIN_EXPERIENCE.AUTO.DESCONSIDERA_LEADS_GERADOS_POR_DISPAROS_ATIVOS_COMO_REMA'
                )
              }}
            </p>
          </AgentField>
          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.CANAIS_ESTRUTURADOS')"
            :helper="
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.CHANNELS_GUIDANCE_HELPER')
            "
            grouped
          >
            <div class="grid gap-2 md:grid-cols-3">
              <label
                v-for="channel in allowedChannelOptions"
                :key="channel.value"
                class="flex min-h-[2.75rem] items-center gap-2 border-b border-n-weak py-2 text-sm font-medium text-n-slate-11 last:border-b-0"
              >
                <input
                  v-model="formModel.allowedChannels"
                  :value="channel.value"
                  type="checkbox"
                  class="size-4 rounded border-n-weak text-n-blue-11"
                />
                {{ channel.label }}
              </label>
            </div>
          </AgentField>

          <div class="flex flex-col gap-4">
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.ORIGENS_CAMPANHAS_ATENDIDAS')"
              :helper="
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.AUDIENCE_GUIDANCE_HELPER')
              "
              control-id="agent-allowed-origins"
            >
              <input
                id="agent-allowed-origins"
                v-model="formModel.allowedOriginsText"
                class="agent-input"
                :placeholder="
                  ce('CAPTAIN_EXPERIENCE.AUTO.INSTAGRAM_SITE_CAMPANHA_X')
                "
                aria-describedby="agent-allowed-origins-description"
              />
            </AgentField>
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.SEGMENTOS_DE_PUBLICO')"
              :helper="
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.AUDIENCE_GUIDANCE_HELPER')
              "
              control-id="agent-audience-scope"
            >
              <input
                id="agent-audience-scope"
                v-model="formModel.audienceScopeRulesText"
                class="agent-input"
                :placeholder="
                  ce('CAPTAIN_EXPERIENCE.AUTO.INVESTIDORES_PRIMEIRA_COMPRA')
                "
                aria-describedby="agent-audience-scope-description"
              />
            </AgentField>
          </div>

          <div class="flex flex-col gap-4">
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.WHATSAPP_DIRETO')"
              control-id="agent-direct-whatsapp-behavior"
            >
              <select
                id="agent-direct-whatsapp-behavior"
                v-model="formModel.directWhatsappBehavior"
                class="agent-input"
              >
                <option value="always_reply">
                  {{ ce('CAPTAIN_EXPERIENCE.AUTO.SEMPRE_RESPONDER') }}
                </option>
                <option value="respect_scope_with_notice">
                  {{
                    ce(
                      'CAPTAIN_EXPERIENCE.AUTO.RESPONDER_RESPEITANDO_O_ESCOPO_COM_AVISO'
                    )
                  }}
                </option>
                <option value="handoff_only">
                  {{
                    ce(
                      'CAPTAIN_EXPERIENCE.AUTO.APENAS_ENCAMINHAR_PARA_A_EQUIPE'
                    )
                  }}
                </option>
              </select>
            </AgentField>
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.LINK_DIRETO_DO_SITE')"
              control-id="agent-website-direct-link-policy"
            >
              <select
                id="agent-website-direct-link-policy"
                v-model="formModel.websiteDirectLinkPolicy"
                class="agent-input"
              >
                <option value="in_scope_only">
                  {{ ce('CAPTAIN_EXPERIENCE.AUTO.APENAS_DENTRO_DO_ESCOPO') }}
                </option>
                <option value="requires_site_code_change">
                  {{
                    ce('CAPTAIN_EXPERIENCE.AUTO.EXIGE_AJUSTE_NO_CODIGO_DO_SITE')
                  }}
                </option>
                <option value="always_allow">
                  {{ ce('CAPTAIN_EXPERIENCE.AUTO.SEMPRE_PERMITIR') }}
                </option>
              </select>
            </AgentField>
          </div>

          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.SCRIPT.FALLBACK_TEAM_LABEL')"
            control-id="agent-fallback-team"
          >
            <select
              id="agent-fallback-team"
              v-model="formModel.fallbackTeamId"
              class="agent-input"
            >
              <option value="">
                {{ ce('CAPTAIN_EXPERIENCE.SCRIPT.FALLBACK_TEAM_NONE') }}
              </option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.name }}
              </option>
            </select>
          </AgentField>

          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.FORA_DO_HORARIO_COMERCIAL')"
            control-id="agent-out-of-hours-behavior"
          >
            <select
              id="agent-out-of-hours-behavior"
              v-model="formModel.outOfHoursBehavior"
              class="agent-input"
            >
              <option value="continuar_atendimento">
                {{
                  ce(
                    'CAPTAIN_EXPERIENCE.AUTO.CONTINUAR_O_ATENDIMENTO_NORMALMENTE'
                  )
                }}
              </option>
              <option value="coletar_e_handoff">
                {{
                  ce(
                    'CAPTAIN_EXPERIENCE.AUTO.COLETAR_CONTEXTO_E_ENCAMINHAR_PARA_A_EQUIPE'
                  )
                }}
              </option>
              <option value="somente_link_agenda">
                {{
                  ce('CAPTAIN_EXPERIENCE.AUTO.ENVIAR_APENAS_O_LINK_DE_AGENDA')
                }}
              </option>
            </select>
          </AgentField>
          <div class="flex flex-wrap gap-2">
            <Button
              v-if="canManage"
              type="submit"
              icon="i-lucide-save"
              :is-loading="isSaving"
              :label="saveLabel"
            />
            <Button
              id="agent-connected-channels"
              type="button"
              variant="ghost"
              icon="i-lucide-inbox"
              @click="emit('openRoute', 'captain_assistants_inboxes_index')"
            >
              {{ ce('CAPTAIN_EXPERIENCE.AUTO.ESCOLHER_CANAIS') }}
            </Button>
          </div>
        </fieldset>
      </form>
    </AgentSection>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.AUTO.CHECKLIST_ANTES_DE_ATIVAR')"
    >
      <AgentReadinessChecklist :items="readinessChecklist" />
    </AgentSection>

    <AgentSection
      :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_HISTORY_TITLE')"
    >
      <p class="mb-4 text-sm leading-relaxed text-n-slate-11">
        {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_HISTORY_DESCRIPTION') }}
      </p>

      <div
        v-if="isLoadingVersions"
        class="flex items-center gap-2 rounded-xl border border-n-weak p-4 text-sm text-n-slate-11"
        role="status"
        aria-live="polite"
      >
        <span
          class="i-lucide-loader-circle size-4 animate-spin"
          aria-hidden="true"
        />
        {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_HISTORY_LOADING') }}
      </div>
      <AgentNotice
        v-else-if="versionsError"
        tone="error"
        :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_HISTORY_ERROR_TITLE')"
        :description="versionsError"
        action-icon="i-lucide-refresh-cw"
        :action-label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
        @action="emit('retryVersions')"
      />
      <div
        v-else-if="versions.length"
        class="flex flex-col divide-y divide-n-weak"
      >
        <article
          v-for="version in versions"
          :key="version.id"
          class="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-medium text-n-slate-12">
                {{
                  version.name ||
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_LABEL', {
                    id: version.id,
                  })
                }}
              </p>
              <span
                v-if="version.runtime_effective"
                class="rounded-full bg-n-teal-9/10 px-2 py-0.5 text-xs font-semibold text-n-teal-11"
              >
                {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_IN_USE') }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-slate-10">
              {{
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_SOURCE', {
                  source: version.management_source || version.source_type,
                })
              }}
            </p>
          </div>
          <time
            v-if="formatApiDateTime(version.published_at || version.created_at)"
            :datetime="
              apiDateTimeValue(version.published_at || version.created_at)
            "
            class="shrink-0 text-xs text-n-slate-10"
          >
            {{ formatApiDateTime(version.published_at || version.created_at) }}
          </time>
        </article>
      </div>
      <p
        v-else
        class="rounded-xl border border-dashed border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
      >
        {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.VERSION_HISTORY_EMPTY') }}
      </p>
    </AgentSection>
  </section>
</template>

<style scoped>
.agent-input {
  @apply min-h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2.5 text-sm leading-normal text-n-slate-12 outline-none transition placeholder:text-n-slate-10 hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20;
}

.agent-mode-option {
  @apply min-h-24 cursor-pointer rounded-lg border px-3 py-2.5 text-left transition focus-within:ring-2 focus-within:ring-n-brand focus-within:ring-offset-2;
}

.agent-segment-option {
  @apply flex min-h-11 cursor-pointer items-center justify-center rounded-lg border px-3 py-2 text-center text-sm font-medium transition focus-within:ring-2 focus-within:ring-n-brand focus-within:ring-offset-2;
}
</style>
